import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema, faqSchema } from "@/lib/useSEO";
import { FAQ_ITEMS } from "@/lib/faqData";
import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle } from "lucide-react";

export default function FAQ() {
  useSEO({
    title: "PixelEmpire AI — Frequently Asked Questions",
    description:
      "Answers to every common question about PixelEmpire AI — pricing, permanence, SEO backlinks, refunds, editing your ad, and why only 10,000 blocks will ever exist.",
    path: "/faq",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "FAQ", url: "/faq" },
      ]),
      faqSchema(FAQ_ITEMS),
    ],
  });

  return (
    <PageShell testId="page-faq">
      <div className="pill" data-testid="faq-pill">
        <HelpCircle className="w-3.5 h-3.5" /> <span>Frequently asked questions</span>
      </div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="faq-h1">
        Everything you need to know about <span className="text-gradient">PixelEmpire</span>
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl">
        Still stuck? Skim these first — they cover 95% of the questions we get in support.
      </p>

      <div className="mt-10 space-y-3" data-testid="faq-list">
        {FAQ_ITEMS.map((f, i) => (
          <details
            key={f.q}
            className="glass p-5 sm:p-6 rounded-xl group"
            data-testid={`faq-item-${i}`}
            open={i === 0}
          >
            <summary className="cursor-pointer font-semibold text-white text-base sm:text-lg flex items-start justify-between gap-4">
              <span>
                <span className="text-[var(--fg-muted)] font-mono text-xs mr-2">Q{i + 1}.</span>
                {f.q}
              </span>
              <span className="text-[var(--fg-muted)] text-xl leading-none flex-shrink-0 group-open:rotate-45 transition">
                +
              </span>
            </summary>
            <p className="mt-3 text-sm sm:text-base text-[var(--fg-muted)] leading-relaxed">{f.a}</p>
          </details>
        ))}
      </div>

      <section className="mt-14 glass p-6 rounded-2xl text-center">
        <h2 className="font-display text-2xl text-white">Still have questions?</h2>
        <p className="mt-2 text-sm text-[var(--fg-muted)]">
          We answer every email within 24 hours.
        </p>
        <div className="mt-4 flex justify-center gap-3 flex-wrap">
          <Link to="/contact" className="btn-primary" data-testid="faq-cta-contact">
            Contact us <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/how-it-works" className="btn-ghost">How it works</Link>
          <Link to="/pricing" className="btn-ghost">See pricing</Link>
        </div>
      </section>
    </PageShell>
  );
}
